import React, { useState } from 'react';
import { Bar } from 'react-chartjs-2';
import { DocumentDuplicateIcon, CheckIcon } from '@heroicons/react/24/outline';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

export const PayAnalysisContent = ({ 
  payAnalysis, 
  session, 
  project, 
  projectConfig, 
  segments, 
  payTableName 
}) => {
  const [copiedColumn, setCopiedColumn] = useState(null);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatStation = (station) => {
    if (!station) return 'N/A';
    const hundreds = Math.floor(station / 100);
    const remainder = station % 100;
    return `${hundreds}+${remainder.toFixed(1).padStart(4, '0')}`;
  };

  const copyColumnToClipboard = async (columnKey) => {
    try {
      let columnData = [];
      
      // Add column header
      const columnHeaders = {
        segment: 'Segment',
        startStation: 'Start Station',
        length: 'Length (ft)',
        mri: 'MRI',
        payFactor: 'Pay Factor',
        basePayment: 'Base Payment',
        adjustment: 'Adjustment',
        netPayment: 'Net Payment',
        status: 'Status'
      };
      
      columnData.push(columnHeaders[columnKey]);
      
      // Add column values
      payAnalysis?.lots?.forEach((lot) => {
        let value;
        
        switch (columnKey) {
          case 'segment':
            value = lot.lotNumber;
            break;
          case 'startStation':
            value = formatStation(lot.startStation);
            break;
          case 'length':
            value = (lot.lengthFt || lot.actual_length_ft)?.toFixed(1) || 'N/A';
            break;
          case 'mri':
            value = lot.mri.toFixed(1);
            break;
          case 'payFactor':
            value = lot.payFactor.toFixed(3);
            break;
          case 'basePayment':
            value = lot.basePayment.toFixed(2);
            break;
          case 'adjustment':
            value = lot.dollarAdjustment.toFixed(2);
            break;
          case 'netPayment':
            value = lot.adjustedPayment.toFixed(2);
            break;
          case 'status':
            value = lot.requiresCorrection ? 'Correction' : 
                   lot.payFactor > 1.0 ? 'Bonus' :
                   lot.payFactor < 1.0 ? 'Penalty' : 'Neutral';
            break;
          default:
            value = '';
        }
        
        columnData.push(value);
      });
      
      // Join with newlines for Excel compatibility
      const textData = columnData.join('\n');
      
      await navigator.clipboard.writeText(textData);
      
      // Show success feedback
      setCopiedColumn(columnKey);
      setTimeout(() => setCopiedColumn(null), 2000);
      
    } catch (err) {
      console.error('Failed to copy column:', err);
      alert('Failed to copy column data');
    }
  };

  const handleExportAnalysis = async () => {
    try {
      // Create CSV content
      const csvHeaders = [
        'Lot Number', 'Start Station (ft)', 'End Station (ft)', 'Length (ft)', 
        'MRI (in/mi)', 'Pay Factor', 'Tonnage', 'Base Payment ($)', 
        'Adjusted Payment ($)', 'Dollar Adjustment ($)', 'Requires Correction',
        'Target MRI', 'Note'
      ];
      
      const csvRows = payAnalysis.lots.map(lot => [
        lot.lotNumber,
        lot.startStation.toFixed(2),
        lot.endStation.toFixed(2),
        lot.lengthFt.toFixed(2),
        lot.mri.toFixed(2),
        lot.payFactor.toFixed(3),
        lot.tonnage.toFixed(2),
        lot.basePayment.toFixed(2),
        lot.adjustedPayment.toFixed(2),
        lot.dollarAdjustment.toFixed(2),
        lot.requiresCorrection ? 'Yes' : 'No',
        lot.targetMri || '',
        lot.note || ''
      ]);

      const csvContent = [
        csvHeaders.join(','),
        ...csvRows.map(row => row.map(cell => 
          typeof cell === 'string' && cell.includes(',') ? `"${cell}"` : cell
        ).join(','))
      ].join('\n');

      // Download file
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${project?.name}_${session?.filename}_PayAnalysis.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting analysis:', err);
      alert('Failed to export data: ' + err.message);
    }
  };

  // Column Header with Copy Function
  const CopyableColumnHeader = ({ columnKey, children, className = "" }) => (
    <th className={`relative group ${className}`}>
      <div className="flex items-center justify-between">
        <span className="px-3 py-2 text-left text-xs text-gray-500 uppercase">
          {children}
        </span>
        <button
          onClick={() => copyColumnToClipboard(columnKey)}
          className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 p-1 rounded hover:bg-gray-100 mr-2"
          title={`Copy ${children} column`}
        >
          {copiedColumn === columnKey ? (
            <CheckIcon className="h-4 w-4 text-green-600" />
          ) : (
            <DocumentDuplicateIcon className="h-4 w-4 text-gray-500 hover:text-gray-700" />
          )}
        </button>
      </div>
    </th>
  );

  // Summary Cards Component
  const SummaryCards = () => (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
      {/* Base Payment */}
      <div className="bg-white p-4 rounded border border-gray-200">
        <div className="text-xs text-gray-500 mb-1">BASE PAYMENT</div>
        <div className="text-xl font-medium text-gray-900">
          {formatCurrency(payAnalysis?.summary?.totalBasePayment || 0)}
        </div>
      </div>

      {/* Adjustment */}
      <div className="bg-white p-4 rounded border border-gray-200">
        <div className="text-xs text-gray-500 mb-1">ADJUSTMENT</div>
        <div className={`text-xl font-medium ${
          (payAnalysis?.summary?.totalDollarAdjustment || 0) > 0 ? 'text-gray-900' : 
          (payAnalysis?.summary?.totalDollarAdjustment || 0) < 0 ? 'text-gray-900' : 'text-gray-900'
        }`}>
          {(payAnalysis?.summary?.totalDollarAdjustment || 0) >= 0 ? '+' : ''}
          {formatCurrency(payAnalysis?.summary?.totalDollarAdjustment || 0)}
        </div>
      </div>

      {/* Net Payment */}
      <div className="bg-white p-4 rounded border border-gray-200">
        <div className="text-xs text-gray-500 mb-1">NET PAYMENT</div>
        <div className="text-xl font-medium text-gray-900">
          {formatCurrency(payAnalysis?.summary?.totalAdjustedPayment || 0)}
        </div>
      </div>

      {/* Avg Pay Factor */}
      <div className="bg-white p-4 rounded border border-gray-200">
        <div className="text-xs text-gray-500 mb-1">AVG PAY FACTOR</div>
        <div className="text-xl font-medium text-gray-900">
          {payAnalysis?.summary?.avgPayFactor?.toFixed(3) || 'N/A'}
        </div>
      </div>

      {/* Bonus Lots */}
      <div className="bg-white p-4 rounded border border-gray-200">
        <div className="text-xs text-gray-500 mb-1">BONUS LOTS</div>
        <div className="text-xl font-medium text-gray-900">
          {payAnalysis?.summary?.bonusLots || 0}
        </div>
      </div>

      {/* Correction Lots */}
      <div className="bg-white p-4 rounded border border-gray-200">
        <div className="text-xs text-gray-500 mb-1">CORRECTIONS</div>
        <div className="text-xl font-medium text-gray-900">
          {payAnalysis?.summary?.correctionRequiredLots || 0}
        </div>
      </div>
    </div>
  );

  // Pay Factor Chart Component
  const PayFactorChart = () => {
    const chartData = payAnalysis?.lots?.map((lot, index) => ({
      lot: `Lot ${lot.lotNumber}`,
      station: formatStation(lot.startStation),
      payFactor: lot.payFactor,
      dollarAdjustment: lot.dollarAdjustment,
      mri: lot.mri,
      requiresCorrection: lot.requiresCorrection
    })) || [];

    const data = {
      labels: chartData.map(item => item.station),
      datasets: [
        {
          label: 'Pay Factor',
          data: chartData.map(item => item.payFactor),
          backgroundColor: chartData.map(item => {
            if (item.requiresCorrection) return '#dc2626'; // Red for correction required
            if (item.payFactor > 1.0) return '#16a34a';    // Green for bonus
            if (item.payFactor < 1.0) return '#f97316';    // Orange for penalty
            return '#6b7280';                              // Gray for neutral
          }),
          borderWidth: 0,
          borderRadius: 2,
          borderSkipped: false,
        }
      ]
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          backgroundColor: 'white',
          titleColor: '#1f2937',
          bodyColor: '#6b7280',
          borderColor: '#e5e7eb',
          borderWidth: 1,
          cornerRadius: 6,
          displayColors: false,
          callbacks: {
            title: function(context) {
              const dataIndex = context[0].dataIndex;
              return `Station ${chartData[dataIndex].station}`;
            },
            label: function(context) {
              const dataIndex = context.dataIndex;
              const item = chartData[dataIndex];
              return [
                `Pay Factor: ${item.payFactor.toFixed(3)}`,
                `MRI: ${item.mri.toFixed(1)} in/mi`,
                `Adjustment: ${formatCurrency(item.dollarAdjustment)}`,
                ...(item.requiresCorrection ? ['Requires Correction'] : [])
              ];
            }
          }
        }
      },
      scales: {
        x: {
          grid: {
            color: '#f3f4f6',
            drawBorder: false
          },
          ticks: {
            color: '#6b7280',
            font: {
              size: 12
            },
            maxRotation: 45,
            minRotation: 45
          },
          border: {
            display: false
          }
        },
        y: {
          min: 0.9,
          max: 1.1,
          grid: {
            color: '#f3f4f6',
            drawBorder: false
          },
          ticks: {
            color: '#6b7280',
            font: {
              size: 12
            }
          },
          border: {
            display: false
          }
        }
      }
    };

    return (
      <div className="bg-white p-6 rounded border border-gray-200 mb-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Pay Factors by Segment</h3>
            <p className="text-sm text-gray-500 mt-1">{chartData.length} segments analyzed</p>
          </div>
          <button
            onClick={handleExportAnalysis}
            className="px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
          >
            Export CSV
          </button>
        </div>
        <div style={{ height: '320px' }}>
          <Bar data={data} options={options} />
        </div>
      </div>
    );
  };

  // Correction Analysis Component
  const CorrectionAnalysis = () => {
    const correctionLots = payAnalysis?.lots?.filter(lot => lot.requiresCorrection) || [];
    
    if (correctionLots.length === 0) return null;

    const currentPenalty = correctionLots.reduce((sum, lot) => sum + lot.dollarAdjustment, 0);

    return (
      <div className="bg-gray-50 border border-gray-200 rounded p-6 mb-6">
        <h3 className="text-base font-medium text-gray-900 mb-4">Correction Required</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <div className="text-sm text-gray-500 mb-1">LOTS REQUIRING CORRECTION</div>
            <div className="text-xl font-medium text-gray-900">{correctionLots.length}</div>
          </div>
          <div>
            <div className="text-sm text-gray-500 mb-1">CURRENT PENALTY</div>
            <div className="text-xl font-medium text-gray-900">{formatCurrency(Math.abs(currentPenalty))}</div>
          </div>
          <div>
            <div className="text-sm text-gray-500 mb-1">AVERAGE TARGET MRI</div>
            <div className="text-xl font-medium text-gray-900">
              {correctionLots.length > 0 
                ? (correctionLots.reduce((sum, lot) => sum + (lot.targetMri || 70), 0) / correctionLots.length).toFixed(1)
                : 'N/A'
              } in/mi
            </div>
          </div>
        </div>
        <p className="text-sm text-gray-600 mt-4 pt-4 border-t border-gray-200">
          Sections with high MRI values require corrective grinding to meet specification requirements.
        </p>
      </div>
    );
  };

  // Lot Results Table Component
  const LotResultsTable = () => (
    <div className="bg-white border border-gray-200 rounded mb-6">
      <div className="p-4 border-b border-gray-200">
        <h3 className="text-base font-medium text-gray-900">Segment Analysis</h3>
        <p className="text-sm text-gray-500 mt-1">
          {payAnalysis?.lots?.length || 0} segments with actual lengths • Hover column headers to copy data
        </p>
      </div>
      
      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead className="bg-gray-50">
            <tr>
              <CopyableColumnHeader columnKey="segment">Segment</CopyableColumnHeader>
              <CopyableColumnHeader columnKey="startStation">Start Station</CopyableColumnHeader>
              <CopyableColumnHeader columnKey="length">Length (ft)</CopyableColumnHeader>
              <CopyableColumnHeader columnKey="mri">MRI</CopyableColumnHeader>
              <CopyableColumnHeader columnKey="payFactor">Pay Factor</CopyableColumnHeader>
              <CopyableColumnHeader columnKey="basePayment">Base Payment</CopyableColumnHeader>
              <CopyableColumnHeader columnKey="adjustment">Adjustment</CopyableColumnHeader>
              <CopyableColumnHeader columnKey="netPayment">Net Payment</CopyableColumnHeader>
              <CopyableColumnHeader columnKey="status">Status</CopyableColumnHeader>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {payAnalysis?.lots?.map((lot, index) => (
              <tr key={lot.lotNumber} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                <td className="px-3 py-2 text-sm font-medium text-gray-900">
                  {lot.lotNumber}
                </td>
                <td className="px-3 py-2 text-sm font-mono text-gray-900">
                  {formatStation(lot.startStation)}
                </td>
                <td className="px-3 py-2 text-sm font-medium text-gray-900">
                  {(lot.lengthFt || lot.actual_length_ft)?.toFixed(1) || 'N/A'}
                </td>
                <td className="px-3 py-2 text-sm font-medium text-gray-900">
                  {lot.mri.toFixed(1)}
                </td>
                <td className="px-3 py-2 text-sm font-medium text-gray-900">
                  {lot.payFactor.toFixed(3)}
                </td>
                <td className="px-3 py-2 text-sm text-gray-900">
                  {formatCurrency(lot.basePayment)}
                </td>
                <td className={`px-3 py-2 text-sm font-medium ${
                  lot.dollarAdjustment > 0 ? 'text-gray-900' : 
                  lot.dollarAdjustment < 0 ? 'text-gray-900' : 'text-gray-900'
                }`}>
                  {lot.dollarAdjustment >= 0 ? '+' : ''}{formatCurrency(lot.dollarAdjustment)}
                </td>
                <td className="px-3 py-2 text-sm font-medium text-gray-900">
                  {formatCurrency(lot.adjustedPayment)}
                </td>
                <td className="px-3 py-2">
                  <div className="flex items-center">
                    {lot.requiresCorrection ? (
                      <>
                        <div className="w-2 h-2 bg-red-500 rounded-full mr-2"></div>
                        <span className="text-xs text-gray-600">Correction</span>
                      </>
                    ) : lot.payFactor > 1.0 ? (
                      <>
                        <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                        <span className="text-xs text-gray-600">Bonus</span>
                      </>
                    ) : lot.payFactor < 1.0 ? (
                      <>
                        <div className="w-2 h-2 bg-orange-500 rounded-full mr-2"></div>
                        <span className="text-xs text-gray-600">Penalty</span>
                      </>
                    ) : (
                      <>
                        <div className="w-2 h-2 bg-gray-400 rounded-full mr-2"></div>
                        <span className="text-xs text-gray-600">Neutral</span>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Configuration Summary */}
      <div className="bg-white border border-gray-200 rounded p-4">
        <h2 className="text-base font-medium text-gray-900 mb-2">Pay Analysis Summary</h2>
        <div className="text-sm text-gray-600 space-x-4">
          <span>{payAnalysis.summary.totalLots} segments</span>
          <span>•</span>
          <span>{payTableName}</span>
          <span>•</span>
          <span>{formatCurrency(projectConfig?.unit_price_per_ton || 0)}/ton</span>
          <span>•</span>
          <span>{payAnalysis.summary.projectCompliance.replace('_', ' ')}</span>
        </div>
      </div>

      {/* Summary Cards */}
      <SummaryCards />

      {/* Correction Analysis */}
      <CorrectionAnalysis />

      {/* Pay Factor Chart */}
      <PayFactorChart />

      {/* Lot Results Table */}
      <LotResultsTable />

      {/* Detailed Statistics */}
      <div className="bg-white p-6 rounded border border-gray-200">
        <h3 className="text-base font-medium text-gray-900 mb-4">Project Statistics</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div>
            <div className="text-xs text-gray-500 mb-1">TOTAL LENGTH</div>
            <div className="text-base font-medium text-gray-900">{payAnalysis.summary.totalLengthMiles.toFixed(2)} miles</div>
          </div>
          <div>
            <div className="text-xs text-gray-500 mb-1">TOTAL TONNAGE</div>
            <div className="text-base font-medium text-gray-900">{payAnalysis.summary.totalTonnage.toFixed(1)} tons</div>
          </div>
          <div>
            <div className="text-xs text-gray-500 mb-1">AVERAGE MRI</div>
            <div className="text-base font-medium text-gray-900">{payAnalysis.summary.avgMri.toFixed(1)} in/mi</div>
          </div>
          <div>
            <div className="text-xs text-gray-500 mb-1">OVERALL QUALITY</div>
            <div className="text-base font-medium text-gray-900 capitalize">{payAnalysis.summary.overallQuality.replace('_', ' ')}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500 mb-1">PAY FACTOR RANGE</div>
            <div className="text-base font-medium text-gray-900">
              {payAnalysis.summary.minPayFactor.toFixed(3)} – {payAnalysis.summary.maxPayFactor.toFixed(3)}
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500 mb-1">CORRECTION RATE</div>
            <div className="text-base font-medium text-gray-900">
              {payAnalysis.summary.correctionRequiredLots} lots ({payAnalysis.summary.correctionPercentage.toFixed(1)}%)
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500 mb-1">COMPLIANCE STATUS</div>
            <div className="text-base font-medium text-gray-900 capitalize">{payAnalysis.summary.projectCompliance.replace('_', ' ')}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500 mb-1">ANALYSIS METHOD</div>
            <div className="text-base font-medium text-gray-900">{payAnalysis.metadata.pay_table}</div>
          </div>
        </div>
      </div>
    </div>
  );
};